import React, { FC, useState, useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Appearance, Button } from '@src/components/Button';
import {
  ApplicantRecruiterStateEnum,
  CompanyFeatureFlagTypeEnum,
  useBulkCreateVerifiedUserApplicantsMutation,
  useOpeningQueryQuery,
  useSessionQuery,
} from '@src/graphql/generated';
import { createLink } from '@src/util/createLink';
import { FeaturedTalentCard, Skeleton } from '@src/containers/Feed/FeaturedTalentCard';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import { usePrevious } from '@src/customHooks/usePrevious';
import { getInviteMessage } from '@src/util/getInviteMessage';
import { setModalContent } from '@src/redux/actions/modal';
import { useAppDispatch } from '@src/redux';
import { InviteToInterview } from '@src/components/VerifyTalentCard/InviteToInterview';
import { FUNNEL_ROUTE, PUBLISHED_OPENING_APPLICANTS } from '@src/Routes/links';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { PublishedOpeningApplicantsHeader } from './PublishedOpeningApplicantsHeader';
import { usePublishedOpeningRecommendedUsers } from './hooks';
import './styles.scss';

export const PublishedOpeningApplicants: FC = () => {
  const history = useHistory();

  const match = useRouteMatch<{ openingId?: string }>({
    path: PUBLISHED_OPENING_APPLICANTS,
    sensitive: true,
    strict: true,
  });

  const openingId = match?.params?.openingId;

  const dispatch = useAppDispatch();

  const { data: sessionData } = useSessionQuery();

  const company = sessionData?.company;
  const recruiter = sessionData?.currentRecruiter;

  const [selectedApplicantIds, setSelectedApplicantIds] = useState<string[]>([]);

  const [bulkCreateVerifiedUserApplicants] = useBulkCreateVerifiedUserApplicantsMutation({
    onCompleted: () => {
      if (company?.featureFlags.includes(CompanyFeatureFlagTypeEnum.SOURCING_PAGE)) {
        history.push(
          createLink({
            link: FUNNEL_ROUTE,
            params: {
              openingId: openingId,
            },
          }),
        );
      } else {
        history.push(createLink({ link: FUNNEL_ROUTE, params: { openingId } }));
      }
    },
    onError: () => {
      dispatch(setErrorFlashMessage());
    },
  });

  const { feed, isInitialLoading, isFetchMoreLoading, isLoading, hasMoreResults, loadMore } =
    usePublishedOpeningRecommendedUsers({
      openingId: openingId,
      recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
    });
  const { data: openingData, loading: isOpeningLoading } = useOpeningQueryQuery({ variables: { openingId } });

  const openingPitchMessage = openingData?.opening.pitch;
  const openingTitle = openingData?.opening.title;

  const prevFeedLength = usePrevious(feed.length);

  useEffect(() => {
    trackEvent({
      context: MetricContext.PUBLISHED_OPENING_APPLICANTS_BULK_APPLY_VIEW,
      interaction: MetricAction.VIEW_PUBLISHED_OPENING_APPLICANTS_BULK_APPLY_PAGE,
    });

    return () => {
      trackEvent({
        context: MetricContext.PUBLISHED_OPENING_APPLICANTS_BULK_APPLY_VIEW,
        interaction: MetricAction.LEAVE_PUBLISHED_OPENING_APPLICANTS_BULK_APPLY_PAGE,
      });
    };
  }, []);

  useEffect(() => {
    if (!isInitialLoading) {
      const userIds = feed.map(it => it.user.id);
      setSelectedApplicantIds(userIds);
    }
  }, [isInitialLoading]);

  useEffect(() => {
    if (!isFetchMoreLoading) {
      const userIds = feed.slice(prevFeedLength, feed.length).map(it => it.user.id);
      setSelectedApplicantIds([...selectedApplicantIds, ...userIds]);
    }
  }, [isFetchMoreLoading, prevFeedLength]);

  const toggleSelect = (userId: string): void => {
    if (selectedApplicantIds.includes(userId)) {
      setSelectedApplicantIds(prevSelectedApplicantIds =>
        prevSelectedApplicantIds.filter(applicantId => applicantId !== userId),
      );
    } else {
      setSelectedApplicantIds([userId, ...selectedApplicantIds]);
    }
  };

  const onCardClick = (userId: string): void => {
    dispatch(
      setModalContent({
        className: 'vt-invite-modal-wrapper',
        config: {
          shouldCloseOnEsc: true,
          shouldCloseOnOverlayClick: true,
          withoutPadding: true,
        },
        content: (
          <InviteToInterview
            modalOptions={{
              shouldOnlyShowProfile: true,
            }}
            userId={userId}
          />
        ),
      }),
    );
  };

  const onInviteClick = (): void => {
    const inputs = selectedApplicantIds.map(userId => ({
      input: {
        message: openingPitchMessage || getInviteMessage(company?.name, openingTitle),
        recruiterState: ApplicantRecruiterStateEnum.ACCEPTED,
      },
      openingId: openingId,
      userId: userId,
    }));

    bulkCreateVerifiedUserApplicants({
      variables: {
        bulkCreateInputs: inputs,
      },
    });

    trackEvent({
      context: MetricContext.PUBLISHED_OPENING_APPLICANTS_BULK_APPLY_VIEW,
      interaction: MetricAction.INVITE_BULK_APPLICANTS,
      payload: {
        'User Ids': inputs.map(user => user.userId),
      },
    });
  };

  return (
    <div className="published-opening-applicants">
      <PublishedOpeningApplicantsHeader />
      <div className="published-opening-applicants__body">
        <div className="published-opening-applicants__body-container">
          <div className="published-opening-applicants__page-copy">
            <h4>Invite at least 7 people to consider your open position</h4>
            <p>
              You can increase your chances of a great hire by inviting the active talents below <br /> to have a
              conversation about your open position.
            </p>
          </div>
          <div className="published-opening-applicants__applicants">
            {!isOpeningLoading && feed.length
              ? feed.map(userData => (
                  <FeaturedTalentCard
                    invitedUserIds={[]}
                    isSelected={selectedApplicantIds.includes(userData.user.id)}
                    key={userData.user.id}
                    onCardClick={() => onCardClick(userData.user.id)}
                    onSelect={() => toggleSelect(userData.user.id)}
                    userData={userData}
                    wrapperClassName="published-opening-applicants__card"
                  />
                ))
              : null}

            {isLoading || isOpeningLoading ? (
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </>
            ) : null}
          </div>
          {!isLoading && hasMoreResults ? (
            <div className="published-opening-applicants__load-more" onClick={loadMore}>
              Load more
            </div>
          ) : null}
        </div>
      </div>
      <div className="published-opening-applicants__footer">
        <div className="published-opening-applicants__footer-container">
          {selectedApplicantIds.length ? (
            <Button isDisabled={!selectedApplicantIds.length} onClick={onInviteClick}>
              Invite selected({selectedApplicantIds.length})
            </Button>
          ) : (
            <Button
              appearance={Appearance.INVISIBLE}
              isLink
              onClick={() => {
                trackEvent({
                  context: MetricContext.PUBLISHED_OPENING_APPLICANTS_BULK_APPLY_VIEW,
                  interaction: MetricAction.SKIP_PUBLISHED_OPENING_APPLICANTS_BULK_APPLY_PAGE,
                });
              }}
              to={createLink({ link: FUNNEL_ROUTE, params: { openingId } })}
            >
              None selected, skip this step
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
